import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Store, 
  Users, 
  DollarSign, 
  Tablet, 
  MapPin, 
  Truck, 
  Megaphone, 
  Headphones,
  CheckCircle2,
  ArrowRight,
  Mail,
  Phone,
  Flame,
  Zap,
  Quote,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const PartnerLanding = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    storeName: '',
    country: '',
    city: '',
    phone: '',
    email: '',
    storeDetails: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState(35);
  const [agreedToExclusivity, setAgreedToExclusivity] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 800) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    setFormData(prev => ({ ...prev, country: value }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.storeName || !formData.country || 
        !formData.city || !formData.phone || !formData.email || !formData.storeDetails) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!agreedToExclusivity) {
      toast.error('Please confirm you understand this is an exclusive opportunity');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        toast.success(result.message || 'Application submitted successfully!');
        // Scroll to top to show confirmation
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error(result.detail || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateMonthlyEarnings = (ordersPerDay) => {
    return ordersPerDay * 10 * 30;
  };

  const getCityStats = (cities) => {
    const available = cities.filter(c => c.status === 'open').length;
    const taken = cities.filter(c => c.status === 'taken').length;
    return { available, taken };
  };

  const citiesByCountry = {
    australia: [
      { name: 'Sydney', state: 'NSW', status: 'taken' },
      { name: 'Newcastle', state: 'NSW', status: 'taken' },
      { name: 'Canberra', state: 'ACT', status: 'taken' },
      { name: 'Melbourne', state: 'VIC', status: 'taken' },
      { name: 'Dubbo', state: 'NSW', status: 'coming-soon' },
      { name: 'Brisbane', state: 'QLD', status: 'open' },
      { name: 'Perth', state: 'WA', status: 'open' },
      { name: 'Adelaide', state: 'SA', status: 'open' },
      { name: 'Gold Coast', state: 'QLD', status: 'open' },
      { name: 'Geelong', state: 'VIC', status: 'open' },
      { name: 'Wollongong', state: 'NSW', status: 'open' },
      { name: 'Hobart', state: 'TAS', status: 'open' },
      { name: 'Parramatta', state: 'NSW', status: 'open' },
      { name: 'Blacktown', state: 'NSW', status: 'open' },
      { name: 'Dandenong', state: 'VIC', status: 'open' },
      { name: 'Craigieburn', state: 'VIC', status: 'open' },
      { name: 'Sunshine Coast', state: 'QLD', status: 'open' },
      { name: 'Townsville', state: 'QLD', status: 'open' },
      { name: 'Darwin', state: 'NT', status: 'open' },
      { name: 'Cairns', state: 'QLD', status: 'open' },
      { name: 'Ballarat', state: 'VIC', status: 'open' }
    ],
    newzealand: [
      { name: 'Auckland', status: 'taken' },
      { name: 'Wellington', status: 'open' },
      { name: 'Christchurch', status: 'open' },
      { name: 'Hamilton', status: 'coming-soon' },
      { name: 'Tauranga', status: 'open' },
      { name: 'Dunedin', status: 'open' },
      { name: 'Palmerston North', status: 'open' },
      { name: 'Napier-Hastings', status: 'open' },
      { name: 'Nelson', status: 'open' },
      { name: 'Rotorua', status: 'open' }
    ],
    uk: [
      { name: 'Nottingham', status: 'taken' },
      { name: 'London', status: 'open' },
      { name: 'Birmingham', status: 'open' },
      { name: 'Manchester', status: 'open' },
      { name: 'Leicester', status: 'open' },
      { name: 'Bradford', status: 'open' },
      { name: 'Leeds', status: 'open' },
      { name: 'Wolverhampton', status: 'open' },
      { name: 'Coventry', status: 'open' },
      { name: 'Glasgow', status: 'open' },
      { name: 'Southampton', status: 'open' },
      { name: 'Cardiff', status: 'open' }
    ],
    japan: [
      { name: 'Osaka', status: 'taken' },
      { name: 'Tokyo', status: 'open' },
      { name: 'Nagoya', status: 'open' },
      { name: 'Yokohama', status: 'open' },
      { name: 'Fukuoka', status: 'open' },
      { name: 'Kobe', status: 'open' },
      { name: 'Kyoto', status: 'open' },
      { name: 'Sapporo', status: 'open' }
    ]
  };

  const getCityCardStyles = (status) => {
    switch (status) {
      case 'open':
        return {
          card: {
            backgroundColor: '#004AAD',
            borderColor: '#004AAD'
          },
          name: 'text-white font-bold',
          state: { color: '#7CB3FF' },
          badge: {
            backgroundColor: 'white',
            color: '#004AAD'
          },
          badgeText: '🟢 Open',
          showPulse: true
        };
      case 'taken':
        return {
          card: {
            backgroundColor: '#2D2D2D',
            borderColor: '#2D2D2D'
          },
          name: 'text-white font-bold line-through',
          state: { color: '#888888' },
          badge: {
            backgroundColor: '#1A1A1A',
            color: '#888888'
          },
          badgeText: '🔒 Taken',
          showPulse: false
        };
      case 'coming-soon':
        return {
          card: {
            backgroundColor: '#FFF3CD',
            borderColor: '#FFF3CD',
            borderLeft: '4px solid #F5A800'
          },
          name: 'font-bold',
          nameColor: '#1A0F00',
          state: { color: '#8B6914' },
          badge: {
            backgroundColor: 'transparent',
            color: '#F5A800',
            fontWeight: 'bold'
          },
          badgeText: '⏳ Coming Soon',
          showPulse: false
        };
      default:
        return {};
    }
  };

  const benefits = [
    {
      icon: <Tablet className="w-8 h-8" />,
      title: 'A tablet that just works',
      description: "We install it. We set it up. You just look at it each morning to see today's orders."
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'Your city. Your orders. Nobody else.',
      description: 'Exclusive city rights — one partner per city, all orders in your area come to you.'
    },
    {
      icon: <Store className="w-8 h-8" />,
      title: 'Zero risk. Your stock stays yours.',
      description: 'We only touch it when an order comes in. No consignment, no stock transfer.'
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: 'We seal it, we ship it',
      description: "The moment you seal the box, it's our problem. Not yours. We book courier, track, notify."
    },
    {
      icon: <Megaphone className="w-8 h-8" />,
      title: 'We spend to bring you customers',
      description: 'Social ads, app promotions, community campaigns. You spend nothing.'
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: 'They call us, not you',
      description: 'Customer queries, complaints, returns — you never hear about it. We handle everything.'
    }
  ];

  const testimonials = [
    {
      quote: "I was sceptical at first. Within the first week I had 30 extra orders. No extra staff, no extra effort.",
      author: "Rajesh K.",
      location: "Newcastle NSW"
    },
    {
      quote: "The tablet just shows up, the orders come in, I pack them. It's the easiest money my store makes.",
      author: "Priya M.",
      location: "Partner Store Owner"
    },
    {
      quote: "Dipan explained everything clearly. I was live within 3 weeks of applying.",
      author: "Harinder S.",
      location: "Auckland NZ"
    }
  ];

  const howItWorksSteps = [
    {
      step: 1,
      title: 'Share Your Inventory',
      description: 'Share your inventory and base prices — we handle listings, photography, and marketing'
    },
    {
      step: 2,
      title: 'We Bring Orders',
      description: 'We bring online orders from Indian families in your city to you exclusively'
    },
    {
      step: 3,
      title: 'You Pick & Pack',
      description: 'Orders appear on a BOI tablet we install at your store — you pick and pack, we book the courier'
    },
    {
      step: 4,
      title: 'Get Paid Reliably',
      description: 'Invoice us fortnightly, we pay within 7 days — transparent, reliable, no surprises'
    }
  ];

  const faqs = [
    {
      question: 'Do customers know orders come from my store?',
      answer: 'Orders are fulfilled through the Box of India brand. Customers see BOI as their trusted provider, while you remain our exclusive fulfillment partner in your city. This protects your partnership value and ensures consistent brand experience.'
    },
    {
      question: 'Do I need to change my current POS or systems?',
      answer: 'Not at all. We provide a dedicated BOI tablet that operates completely separately from your existing systems. Your current business operations continue exactly as they are — no integration, no changes, no disruption.'
    },
    {
      question: 'What if I run out of stock on a listed product?',
      answer: 'Simply update the stock status on the BOI tablet. You have full control over inventory availability in real-time. We encourage partners to update stock levels regularly to ensure smooth order fulfillment.'
    },
    {
      question: 'Who handles customer returns and complaints?',
      answer: 'We do — completely. All customer communication, complaints, and returns are managed by the BOI support team. You focus on fulfillment, we handle everything customer-facing.'
    },
    {
      question: "Is there a contract? Can I leave if it's not working?",
      answer: "We operate on flexible partnership terms. While we do have a partnership agreement outlining responsibilities, you can exit with reasonable notice if the partnership isn't meeting your expectations. We want partners who want to be with us."
    },
    {
      question: 'Can I still run my own in-store business as normal?',
      answer: 'Absolutely! The BOI partnership is designed to add revenue alongside your existing business, not replace it. Your walk-in customers, your regular operations — everything continues as normal. BOI orders are simply an additional income stream.'
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F0F5FF' }}>
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="mb-6 flex justify-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#004AAD' }}>
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#001F4D' }}>
            Thank You for Applying!
          </h1>
          <p className="text-xl mb-8" style={{ color: '#001F4D', opacity: 0.8 }}>
            Our partnerships adviser Dipan Shah will be in touch within 48 hours.
          </p>
          <Card className="mb-8 border-2" style={{ borderColor: '#004AAD' }}>
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#001F4D' }}>
                Need to reach us sooner?
              </h3>
              <div className="space-y-3 text-left">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5" style={{ color: '#004AAD' }} />
                  <a href="mailto:dipan@boxofindia.app" className="hover:underline" style={{ color: '#004AAD' }}>
                    dipan@boxofindia.app
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5" style={{ color: '#004AAD' }} />
                  <a href="tel:+61430454092" className="hover:underline" style={{ color: '#004AAD' }}>
                    +61 430 454 092
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
          <Button 
            onClick={() => setSubmitted(false)}
            variant="outline"
            size="lg"
            style={{ borderColor: '#004AAD', color: '#004AAD' }}
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0F5FF' }}>
      {/* Welcome Banner */}
      <div 
        className="w-full text-center"
        style={{ 
          background: 'linear-gradient(90deg, #004AAD 0%, #003380 100%)',
          padding: '10px 16px'
        }}
      >
        <span 
          className="text-white"
          style={{ 
            fontSize: '14px', 
            fontWeight: 500,
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: '0.3px'
          }}
        >
          🇮🇳 Welcome to the Box of India Partner Program
        </span>
      </div>

      {/* Announcement Bar */}
      <div className="w-full py-3 px-4 text-center font-semibold text-sm" style={{ backgroundColor: '#001F4D', color: '#F5A800', fontFamily: "'DM Sans', sans-serif" }}>
        <div className="flex items-center justify-center gap-2 max-w-7xl mx-auto">
          <Flame className="w-4 h-4 flex-shrink-0 animate-pulse" />
          <span className="leading-tight">Limited spots available — only 1 partner per city. Once it's gone, it's gone.</span>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      {showStickyBar && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-50 py-3 px-4 shadow-2xl"
          style={{ backgroundColor: '#004AAD' }}
        >
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <span className="text-white font-semibold flex items-center gap-2 text-sm text-center sm:text-left" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#F5A800' }} />
              <span>Your city may still be open — Check availability</span>
            </span>
            <Button 
              onClick={() => scrollToSection('available-cities')}
              style={{ backgroundColor: '#F5A800', color: '#001F4D', fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}
              className="font-semibold hover:opacity-90 whitespace-nowrap text-sm px-6 py-2"
            >
              View Cities
            </Button>
          </div>
        </div>
      )}

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/61430454092?text=Hi%20Dipan%2C%20I%27m%20interested%20in%20becoming%20a%20Box%20of%20India%20partner.%20Can%20we%20chat%3F"
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed z-40 flex items-center justify-center rounded-full shadow-lg transition-all duration-300 hover:scale-110 w-14 h-14 right-4 sm:right-[200px] ${
          showStickyBar ? 'bottom-[130px] sm:bottom-[75px]' : 'bottom-20 sm:bottom-6'
        }`}
        style={{
          backgroundColor: '#25D366',
          animation: 'whatsapp-pulse 2s infinite'
        }}
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="white"
          className="w-7 h-7"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* WhatsApp Pulse Animation */}
      <style>{`
        @keyframes whatsapp-pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
          }
          70% {
            box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
          }
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1739065882919-03c8ae1a3da3?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 31, 77, 0.85)' }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-40">
          <div className="max-w-4xl">
            <h1 className="hero-headline text-white mb-6">
              Turn Your Store Into a <span style={{ color: '#F5A800' }}>Revenue Machine.</span>
            </h1>
            <p className="text-3xl lg:text-4xl font-bold mb-6" style={{ color: '#F5A800' }}>
              While you sleep. While you serve your own customers. While you do nothing different.
            </p>
            <p className="text-xl lg:text-2xl text-white mb-10 leading-relaxed opacity-95 max-w-3xl">
              Box of India brings thousands of Indian families in your city directly to your door — as online orders. You pack them. We handle everything else. You get paid fortnightly.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                size="lg"
                className="button-text px-10 py-8 transition-all duration-300 hover:scale-105 shadow-2xl"
                style={{ backgroundColor: '#004AAD', color: 'white' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#003380'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#004AAD'}
                onClick={() => scrollToSection('application-form')}
              >
                Apply for Your City
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="button-text px-10 py-8 border-3 text-white hover:bg-white hover:text-blue-900 transition-all duration-300"
                style={{ backgroundColor: 'transparent', borderWidth: '3px', borderColor: 'white' }}
                onClick={() => scrollToSection('how-it-works')}
              >
                See How It Works
              </Button>
            </div>

            <div 
              className="rounded-2xl p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 shadow-2xl"
              style={{ backgroundColor: '#004AAD' }}
            >
              <div className="text-center">
                <div className="stat-number mb-2" style={{ color: '#F5A800' }}>$0</div>
                <div className="text-white font-semibold text-lg">Upfront Investment</div>
              </div>
              <div className="text-center">
                <div className="stat-number mb-2" style={{ color: '#F5A800' }}>21</div>
                <div className="text-white font-semibold text-lg">Day Payment Terms</div>
              </div>
              <div className="text-center">
                <div className="stat-number mb-2" style={{ color: '#F5A800' }}>1</div>
                <div className="text-white font-semibold text-lg">Store Per City</div>
              </div>
              <div className="text-center">
                <div className="stat-number mb-2" style={{ color: '#F5A800' }}>3 Weeks</div>
                <div className="text-white font-semibold text-lg">To Go Live</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-28" style={{ backgroundColor: '#E8F0FC' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4" style={{ color: '#001F4D' }}>
              How It Works
            </h2>
            <p className="body-text" style={{ color: '#001F4D', opacity: 0.7 }}>
              Four simple steps to start earning more from your existing inventory
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((item) => (
              <div key={item.step} className="relative">
                <div className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4"
                    style={{ backgroundColor: '#004AAD' }}
                  >
                    {item.step}
                  </div>
                  <h3 className="subsection-heading mb-3" style={{ color: '#001F4D' }}>
                    {item.title}
                  </h3>
                  <p style={{ color: '#001F4D', opacity: 0.7 }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6" style={{ color: '#001F4D' }}>
              You Focus On Your Store.<br />We Handle The Rest. All Of It.
            </h2>
            <p className="text-2xl" style={{ color: '#004AAD' }}>
              Zero disruption. Maximum earnings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="border-3 hover:shadow-2xl transition-all duration-300 hover:scale-105" 
                style={{ borderColor: '#004AAD' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E8F0FC'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <CardContent className="pt-8">
                  <div className="mb-6" style={{ color: '#004AAD' }}>
                    {benefit.icon}
                  </div>
                  <h3 className="subsection-heading mb-4" style={{ color: '#001F4D' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-lg leading-relaxed" style={{ color: '#001F4D', opacity: 0.7 }}>
                    {benefit.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F0F5FF' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4" style={{ color: '#001F4D' }}>
              Why Store Owners Say Yes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-l-4 shadow-lg hover:shadow-2xl transition-all duration-300" style={{ borderLeftColor: '#004AAD' }}>
                <CardContent className="pt-8">
                  <Quote className="w-12 h-12 mb-6" style={{ color: '#F5A800' }} />
                  <p className="testimonial-quote mb-6" style={{ color: '#001F4D' }}>
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t-2 pt-4" style={{ borderColor: '#004AAD' }}>
                    <p className="font-bold text-lg" style={{ color: '#001F4D' }}>
                      {testimonial.author}
                    </p>
                    <p style={{ color: '#001F4D', opacity: 0.6 }}>
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Model */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#001F4D' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-black mb-6 text-white">
              Here Is Exactly What You Earn.
            </h2>
            <p className="text-2xl text-white opacity-90">
              Simple, transparent, no hidden fees.
            </p>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <Card className="overflow-hidden border-4" style={{ borderColor: '#004AAD' }}>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{ backgroundColor: '#004AAD' }}>
                      <tr>
                        <th className="px-6 py-5 text-left text-white font-bold text-lg">Your Base Price</th>
                        <th className="px-6 py-5 text-left text-white font-bold text-lg">BOI Lists At</th>
                        <th className="px-6 py-5 text-left text-white font-bold text-lg">Customer Pays</th>
                        <th className="px-6 py-5 text-left text-white font-bold text-lg">You Receive</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="px-6 py-6 font-bold text-xl" style={{ color: '#001F4D' }}>$10.00</td>
                        <td className="px-6 py-6 text-xl" style={{ color: '#001F4D' }}>$13.00</td>
                        <td className="px-6 py-6 text-xl" style={{ color: '#001F4D' }}>$13.00</td>
                        <td className="px-6 py-6 font-black text-2xl" style={{ color: '#004AAD' }}>$10.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <p className="text-center mt-6 text-white opacity-90 text-lg leading-relaxed max-w-3xl mx-auto">
              You set your base price. We add our margin on top. You always receive your full agreed price — no deductions, no hidden fees.
            </p>
          </div>

          {/* Earnings Calculator */}
          <div className="max-w-5xl mx-auto mb-16">
            <Card className="border-4 overflow-hidden" style={{ borderColor: '#004AAD', backgroundColor: 'white' }}>
              <CardContent className="p-8">
                <h3 className="subsection-heading mb-8 text-center" style={{ color: '#001F4D' }}>
                  💰 See Your Potential Earnings
                </h3>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <Button
                    size="lg"
                    variant={selectedOrders === 20 ? 'default' : 'outline'}
                    onClick={() => setSelectedOrders(20)}
                    className="button-text px-8 py-6"
                    style={selectedOrders === 20 ? { backgroundColor: '#004AAD', color: 'white' } : { borderColor: '#004AAD', color: '#004AAD' }}
                    onMouseEnter={(e) => { if(selectedOrders === 20) e.currentTarget.style.backgroundColor = '#003380' }}
                    onMouseLeave={(e) => { if(selectedOrders === 20) e.currentTarget.style.backgroundColor = '#004AAD' }}
                  >
                    20 orders/day
                  </Button>
                  <Button
                    size="lg"
                    variant={selectedOrders === 35 ? 'default' : 'outline'}
                    onClick={() => setSelectedOrders(35)}
                    className="button-text px-8 py-6"
                    style={selectedOrders === 35 ? { backgroundColor: '#004AAD', color: 'white' } : { borderColor: '#004AAD', color: '#004AAD' }}
                    onMouseEnter={(e) => { if(selectedOrders === 35) e.currentTarget.style.backgroundColor = '#003380' }}
                    onMouseLeave={(e) => { if(selectedOrders === 35) e.currentTarget.style.backgroundColor = '#004AAD' }}
                  >
                    35 orders/day
                  </Button>
                  <Button
                    size="lg"
                    variant={selectedOrders === 50 ? 'default' : 'outline'}
                    onClick={() => setSelectedOrders(50)}
                    className="button-text px-8 py-6"
                    style={selectedOrders === 50 ? { backgroundColor: '#004AAD', color: 'white' } : { borderColor: '#004AAD', color: '#004AAD' }}
                    onMouseEnter={(e) => { if(selectedOrders === 50) e.currentTarget.style.backgroundColor = '#003380' }}
                    onMouseLeave={(e) => { if(selectedOrders === 50) e.currentTarget.style.backgroundColor = '#004AAD' }}
                  >
                    50 orders/day
                  </Button>
                </div>

                <div className="text-center p-5 sm:p-10 rounded-2xl max-w-full overflow-hidden" style={{ backgroundColor: '#E8F0FC' }}>
                  <div 
                    className="mb-4 font-black whitespace-nowrap"
                    style={{ 
                      color: '#F5A800',
                      fontSize: 'clamp(2rem, 10vw, 4rem)',
                      lineHeight: 1.1
                    }}
                  >
                    ${calculateMonthlyEarnings(selectedOrders).toLocaleString()}/mo
                  </div>
                  <p className="text-xl sm:text-2xl font-semibold" style={{ color: '#001F4D' }}>
                    Extra Revenue
                  </p>
                  <p className="text-base sm:text-lg mt-3" style={{ color: '#001F4D', opacity: 0.6 }}>
                    Based on {selectedOrders} orders per day at $10 average partner payout
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="backdrop-blur-md bg-white/10 rounded-xl p-8 border-2 border-white/30 text-center">
              <div className="stat-number mb-3" style={{ color: '#F5A800' }}>14-Day</div>
              <div className="text-white opacity-90 text-lg font-semibold">Invoice Cycle</div>
            </div>
            <div className="backdrop-blur-md bg-white/10 rounded-xl p-8 border-2 border-white/30 text-center">
              <div className="stat-number mb-3" style={{ color: '#F5A800' }}>7 Days</div>
              <div className="text-white opacity-90 text-lg font-semibold">To Payment</div>
            </div>
            <div className="backdrop-blur-md bg-white/10 rounded-xl p-8 border-2 border-white/30 text-center">
              <div className="stat-number mb-3" style={{ color: '#F5A800' }}>$0</div>
              <div className="text-white opacity-90 text-lg font-semibold">Joining Fee</div>
            </div>
            <div className="backdrop-blur-md bg-white/10 rounded-xl p-8 border-2 border-white/30 text-center">
              <div className="stat-number mb-3" style={{ color: '#F5A800' }}>1 Partner</div>
              <div className="text-white opacity-90 text-lg font-semibold">Per City Guaranteed</div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Cities */}
      <section id="available-cities" className="py-20 lg:py-28" style={{ backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-6" style={{ color: '#001F4D' }}>
              Available Cities
            </h2>
            <p className="text-2xl font-bold mb-2" style={{ color: '#F5A800' }}>
              ⚡ Exclusive partnerships — one store per city
            </p>
            <p className="body-text" style={{ color: '#001F4D', opacity: 0.7 }}>
              Your city could still be open. Check now.
            </p>
          </div>

          <Tabs defaultValue="australia" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-3 mb-8 h-auto p-0 bg-transparent">
              <TabsTrigger 
                value="australia" 
                className="px-6 py-3 text-lg font-bold rounded-full border-2 transition-all duration-200 data-[state=active]:border-transparent data-[state=inactive]:bg-white data-[state=inactive]:border-gray-300 data-[state=inactive]:text-gray-700"
                style={{ '--active-bg': '#004AAD' }}
                data-testid="tab-australia"
              >
                Australia
              </TabsTrigger>
              <TabsTrigger 
                value="newzealand" 
                className="px-6 py-3 text-lg font-bold rounded-full border-2 transition-all duration-200 data-[state=active]:border-transparent data-[state=inactive]:bg-white data-[state=inactive]:border-gray-300 data-[state=inactive]:text-gray-700"
                data-testid="tab-newzealand"
              >
                New Zealand
              </TabsTrigger>
              <TabsTrigger 
                value="uk" 
                className="px-6 py-3 text-lg font-bold rounded-full border-2 transition-all duration-200 data-[state=active]:border-transparent data-[state=inactive]:bg-white data-[state=inactive]:border-gray-300 data-[state=inactive]:text-gray-700"
                data-testid="tab-uk"
              >
                United Kingdom
              </TabsTrigger>
              <TabsTrigger 
                value="japan" 
                className="px-6 py-3 text-lg font-bold rounded-full border-2 transition-all duration-200 data-[state=active]:border-transparent data-[state=inactive]:bg-white data-[state=inactive]:border-gray-300 data-[state=inactive]:text-gray-700"
                data-testid="tab-japan"
              >
                Japan
              </TabsTrigger>
            </TabsList>

            {/* Legend Strip */}
            <div className="flex flex-wrap justify-center gap-6 mb-8 p-4 rounded-xl" style={{ backgroundColor: '#F8FAFC' }}>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></span>
                <span className="font-semibold text-sm" style={{ color: '#004AAD' }}>Open</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#666666' }}></span>
                <span className="font-semibold text-sm" style={{ color: '#666666' }}>Taken</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F5A800' }}></span>
                <span className="font-semibold text-sm" style={{ color: '#F5A800' }}>Coming Soon</span>
              </div>
            </div>
            
            {Object.entries(citiesByCountry).map(([country, cities]) => (
              <TabsContent key={country} value={country}>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {cities.map((city) => {
                    const styles = getCityCardStyles(city.status);
                    return (
                      <div 
                        key={city.name}
                        data-testid={`city-card-${city.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`p-4 rounded-xl transition-all duration-300 ${
                          city.status === 'open' ? 'hover:scale-105 hover:shadow-xl shadow-lg' : 
                          city.status === 'coming-soon' ? 'hover:scale-102 shadow-md' : ''
                        }`}
                        style={styles.card}
                      >
                        <div className="flex flex-col gap-2">
                          <div>
                            <span 
                              className={`text-lg ${styles.name}`}
                              style={styles.nameColor ? { color: styles.nameColor } : {}}
                            >
                              {city.name}
                            </span>
                            {city.state && (
                              <span className="block text-sm" style={styles.state}>
                                {city.state}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {styles.showPulse && (
                              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                            )}
                            <span 
                              className="text-xs font-bold px-2 py-1 rounded-full"
                              style={styles.badge}
                            >
                              {styles.badgeText}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 lg:py-28" style={{ backgroundColor: '#E8F0FC' }}>
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="section-heading mb-6" style={{ color: '#001F4D' }}>
              Apply Now — Takes 5 Minutes.
            </h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Zap className="w-6 h-6" style={{ color: '#F5A800' }} />
              <p className="text-xl font-bold" style={{ color: '#004AAD' }}>
                We review every application within 48 hours. Most partners are live within 3 weeks.
              </p>
            </div>
          </div>

          <Card className="border-4 shadow-2xl" style={{ borderColor: '#004AAD' }}>
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="fullName" className="form-label" style={{ color: '#001F4D' }}>Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="mt-2 text-lg p-6 border-2 focus:border-blue-500"
                    style={{ borderColor: '#004AAD' }}
                  />
                </div>

                <div>
                  <Label htmlFor="storeName" className="form-label" style={{ color: '#001F4D' }}>Store Name *</Label>
                  <Input
                    id="storeName"
                    name="storeName"
                    value={formData.storeName}
                    onChange={handleInputChange}
                    required
                    className="mt-2 text-lg p-6 border-2"
                    style={{ borderColor: '#004AAD' }}
                  />
                </div>

                <div>
                  <Label htmlFor="country" className="form-label" style={{ color: '#001F4D' }}>Country *</Label>
                  <Select onValueChange={handleCountryChange} required>
                    <SelectTrigger className="mt-2 text-lg p-6 border-2" style={{ borderColor: '#004AAD' }}>
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Australia">Australia</SelectItem>
                      <SelectItem value="New Zealand">New Zealand</SelectItem>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="Japan">Japan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="city" className="form-label" style={{ color: '#001F4D' }}>City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter your city"
                    required
                    className="mt-2 text-lg p-6 border-2"
                    style={{ borderColor: '#004AAD' }}
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="form-label" style={{ color: '#001F4D' }}>Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="mt-2 text-lg p-6 border-2"
                    style={{ borderColor: '#004AAD' }}
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="form-label" style={{ color: '#001F4D' }}>Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-2 text-lg p-6 border-2"
                    style={{ borderColor: '#004AAD' }}
                  />
                </div>

                <div>
                  <Label htmlFor="storeDetails" className="form-label" style={{ color: '#001F4D' }}>Tell Us About Your Store *</Label>
                  <Textarea
                    id="storeDetails"
                    name="storeDetails"
                    value={formData.storeDetails}
                    onChange={handleInputChange}
                    placeholder="How long have you been running? What do you mainly stock? Rough monthly turnover?"
                    required
                    className="mt-2 min-h-32 text-lg p-4 border-2"
                    style={{ borderColor: '#004AAD' }}
                  />
                </div>

                <div className="flex items-start space-x-3 p-4 rounded-lg" style={{ backgroundColor: 'white' }}>
                  <Checkbox 
                    id="exclusivity" 
                    checked={agreedToExclusivity}
                    onCheckedChange={setAgreedToExclusivity}
                    className="mt-1"
                    style={{ borderColor: '#004AAD' }}
                  />
                  <label
                    htmlFor="exclusivity"
                    className="text-base font-medium leading-relaxed cursor-pointer"
                    style={{ color: '#001F4D' }}
                  >
                    ✓ I understand this is an exclusive opportunity — only one store per city will be accepted.
                  </label>
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full button-text py-8 transition-all duration-300 hover:scale-105 shadow-xl disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ backgroundColor: '#004AAD', color: 'white' }}
                  onMouseEnter={(e) => { if(!isSubmitting) e.currentTarget.style.backgroundColor = '#003380' }}
                  onMouseLeave={(e) => { if(!isSubmitting) e.currentTarget.style.backgroundColor = '#004AAD' }}
                  data-testid="submit-application-btn"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit My Application
                      <ArrowRight className="ml-3 w-6 h-6" />
                    </>
                  )}
                </Button>

                <p className="text-center text-base" style={{ color: '#001F4D', opacity: 0.7 }}>
                  No joining fee. No lock-in. Dipan will call you within 48 hours.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Meet Dipan */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4" style={{ color: '#001F4D' }}>
              Got Questions? Talk To Dipan Directly.
            </h2>
          </div>

          <Card className="border-4 shadow-2xl" style={{ borderColor: '#004AAD' }}>
            <CardContent className="pt-10 pb-10">
              <div className="flex flex-col items-center text-center">
                <div 
                  className="w-40 h-40 rounded-full flex items-center justify-center text-6xl font-black text-white mb-6 shadow-xl"
                  style={{ backgroundColor: '#004AAD' }}
                >
                  DS
                </div>
                
                <h3 className="subsection-heading mb-2" style={{ color: '#001F4D' }}>
                  Dipan Shah
                </h3>
                <p className="text-2xl mb-8 font-bold" style={{ color: '#F5A800' }}>
                  Your BOI Partnerships Adviser
                </p>
                
                <p className="mb-8 text-xl leading-relaxed max-w-2xl" style={{ color: '#001F4D' }}>
                  Dipan has helped store owners across Australia and New Zealand get live on BOI in under 3 weeks. He answers calls, replies to messages, and will walk you through everything — no hard sell, no pressure, just a straight conversation about whether this is right for you.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl mb-8">
                  <a href="mailto:dipan@boxofindia.app">
                    <Button 
                      size="lg"
                      className="w-full button-text py-7 transition-all duration-300 hover:scale-105 shadow-lg"
                      style={{ backgroundColor: '#004AAD', color: 'white' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#003380'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#004AAD'}
                    >
                      <Mail className="mr-3 w-6 h-6" />
                      Email Dipan
                    </Button>
                  </a>
                  <a href="tel:+61430454092">
                    <Button 
                      size="lg"
                      className="w-full button-text py-7 transition-all duration-300 hover:scale-105 shadow-lg"
                      style={{ backgroundColor: '#F5A800', color: '#001F4D' }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      <Phone className="mr-3 w-6 h-6" />
                      Call Dipan Now
                    </Button>
                  </a>
                </div>

                <div className="space-y-3 text-left w-full max-w-md p-6 rounded-xl" style={{ backgroundColor: '#E8F0FC' }}>
                  <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 flex-shrink-0" style={{ color: '#004AAD' }} />
                    <a 
                      href="mailto:dipan@boxofindia.app" 
                      className="text-lg hover:underline font-semibold break-all"
                      style={{ color: '#004AAD' }}
                    >
                      dipan@boxofindia.app
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-6 h-6 flex-shrink-0" style={{ color: '#004AAD' }} />
                    <a 
                      href="tel:+61430454092" 
                      className="text-lg hover:underline font-semibold"
                      style={{ color: '#004AAD' }}
                    >
                      +61 430 454 092
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28" style={{ backgroundColor: '#F0F5FF' }}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="section-heading mb-4" style={{ color: '#001F4D' }}>
              Common Questions
            </h2>
            <p className="body-text" style={{ color: '#001F4D', opacity: 0.7 }}>
              Everything you need to know about partnering with BOI
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-2 mb-4 rounded-lg px-6" style={{ borderColor: '#004AAD' }}>
                <AccordionTrigger className="text-left faq-question hover:no-underline py-6 data-[state=open]:text-blue-700" style={{ color: '#001F4D' }}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-lg leading-relaxed pb-6" style={{ color: '#001F4D', opacity: 0.7 }}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 lg:py-32" style={{ backgroundColor: '#004AAD' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="section-heading text-white mb-6 leading-tight">
            Your city might still be available.
          </h2>
          <p className="text-2xl text-white mb-10 opacity-95 font-semibold">
            Takes 5 minutes to apply. Dipan will be in touch within 48 hours.
          </p>
          <Button 
            size="lg"
            className="button-text px-12 py-8 border-4 transition-all duration-300 hover:scale-105 shadow-2xl"
            style={{ backgroundColor: '#F5A800', color: '#001F4D', borderColor: '#F5A800' }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            onClick={() => scrollToSection('application-form')}
          >
            Apply Now
            <ArrowRight className="ml-3 w-7 h-7" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12" style={{ backgroundColor: '#001F4D' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white opacity-80">
            © 2025 Box of India. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PartnerLanding;
